import {
  Badge,
  BadgeVariantEnum,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  RelevantPeopleIcon,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useFeaturedPeople } from '@/features/featured-people/api/get-featured-people';
import { PeopleCatalogueListbox } from '@/features/featured-people/components/PeopleCatalogueListbox';
import { TFeaturedPeopleType } from '@/features/featured-people/types';
import { TFilterProps } from '@/types';

import { TActiveOptions } from '../active-options/active-options';
import {
  BroadcastCatalogueActiveOptions,
  TBroadcastCatalogueRequest,
} from '../active-options/BroadcastCatalogueActiveOptions';
import { TUseCatalogueOptions } from '../active-options/CatalogueActiveOptions';

const toPersonItem = (person: TFeaturedPeopleType) => ({
  value: person.id,
  image: person.parameters?.main_logo_presigned_url,
  label: person.name,
});

const usePeopleOptions: TUseCatalogueOptions<TFeaturedPeopleType, TBroadcastCatalogueRequest> = (
  request,
  config
) =>
  useFeaturedPeople({
    params:
      'active' in request
        ? { idIn: request.active, limit: request.active.length }
        : { idIn: request.idIn, limit: request.idIn.length },
    config,
  });

type MentionFilterSegmentPeopleProps = TFilterProps & {
  value: string[];
  onChange: (next: string[]) => void;
  activeOptions?: TActiveOptions;
};

export function MentionFilterSegmentPeople({
  isOpen,
  isDark,
  showLabel,
  onToggleFilter,
  onClose,
  className = '',
  value,
  onChange,
  activeOptions,
}: MentionFilterSegmentPeopleProps) {
  const { t } = useTranslation();

  const ToolTipWrapper = ({ children }: { children: React.ReactNode }) => {
    if (showLabel) return <>{children}</>;
    return (
      <Tooltip help={t('Personas de interés')} grow>
        {children}
      </Tooltip>
    );
  };

  return (
    <div className="relative w-full md:w-auto">
      <div
        onClick={onToggleFilter}
        className={clsx(
          'group flex w-full cursor-pointer items-center border-b-[1px] border-stroke-30-1 p-1',
          { 'border-text-medium-20': isOpen && !isDark },
          { 'hover:border-text-medium-20': !isDark },
          { 'border-text-neutral-10 hover:bg-reverse-opacity-05/10': isDark },
          { 'bg-reverse-opacity-05/10': isOpen && isDark },
          className
        )}
      >
        <ToolTipWrapper>
          <RelevantPeopleIcon
            className={clsx(
              'mr-1',
              {
                'text-text-medium-10': !isOpen && !isDark,
                'text-accent-active-primary-40': isOpen && !isDark,
              },
              {
                'text-text-neutral-10': isDark,
              },
              {
                'group-hover:text-accent-active-primary-40': !isDark,
              }
            )}
          />
        </ToolTipWrapper>
        {showLabel && (
          <Typography
            fontSize={FontSizeEnum.Sm}
            weight={FontWeightEnum.Medium}
            color={isDark ? FontColorEnum.Neutral : FontColorEnum.Medium}
            className={clsx(
              'select-none pr-size-4',
              {
                'text-text-medium-20': isOpen && !isDark,
              },
              {
                'group-hover:text-text-medium-20': !isDark,
              }
            )}
          >
            {t('Personas de interés')}
          </Typography>
        )}
        {value.length > 0 && (
          <Badge
            variant={!isDark ? BadgeVariantEnum.DefaultFilled : BadgeVariantEnum.NeutralFilled}
            className="py-3"
          >
            + {value.length}
          </Badge>
        )}
      </div>
      {isOpen && (
        <div className="left-0 top-[120%] z-50 w-full bg-neutral-10 p-3 md:absolute md:min-w-[420px] md:shadow-full-medium">
          {activeOptions?.source === 'programBroadcast' ? (
            <BroadcastCatalogueActiveOptions
              activeOptions={activeOptions}
              useOptions={usePeopleOptions}
              toItem={toPersonItem}
              dimension="people"
              isDark={isDark}
              selected={value}
              onChange={onChange}
              onClose={onClose}
            />
          ) : (
            <PeopleCatalogueListbox
              mode="multiple"
              isDark={isDark}
              value={value}
              onChange={onChange}
              onClose={onClose}
              activeOptions={activeOptions}
              unresolvedLabel={t('(no disponible)')}
            />
          )}
        </div>
      )}
    </div>
  );
}
